import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  actionsWidth,
  setsColumnWidth,
  sideColumnWidth,
  typeColumnWidth,
} from '../../../../shared/constants';
import { IExerciseOption } from '../../../../types/interfaces/exercise-option';
import { IProgramSet } from '../../../../types/interfaces/program-set';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { getSetItemKey } from '../../../../utils/training/set-utils';
import { getColumnOptions } from '../../../../utils/ui/column-size-utils';
import SetItemEdit from '../SetItemEdit/SetItemEdit';
import { IUseSetGroupEditTable, SetGroupFieldName } from '../interfaces';
import { useStyles } from './SetItemListEdit.styles';
import { useSetListEditComponent } from './useSetListEditComponent';
import { useSetTableCalculation } from './useSetTableCalculation';

interface ISetItemsListEditProps {
  set: IProgramSet | ITrainingSet;
  useSetGroupEditTable: IUseSetGroupEditTable<IProgramSet | ITrainingSet>;
}

function SetItemsListEdit(props: ISetItemsListEditProps) {
  const { set, useSetGroupEditTable } = props;
  const { mode } = useSetGroupEditTable;
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const setItemsFieldName = SetGroupFieldName[mode].setItemsFieldName;
  const { exerciseOptions, orderedOptions } = useSetListEditComponent(
    set[setItemsFieldName]?.[0],
    mode,
  );
  const { columnWidth, ref } = useSetTableCalculation({
    optionColumnsCount: orderedOptions?.length,
    splitBySide: set?.exercise?.splitBySide,
    hideSetsCount: useSetGroupEditTable.options.hideSetsCount,
    showSetItemStatus: useSetGroupEditTable.options.showSetItemStatus,
  });

  return (
    <Box className={classes.setItemListWrapper} ref={ref}>
      <Box
        display="flex"
        alignItems="center"
        className={classes.setItemListHeader}>
        <Box
          {...getColumnOptions(typeColumnWidth)}
          className={classes.headerTextWrapper}>
          <Typography variant="label-text-md">{t('common.type')}</Typography>
        </Box>
        {!useSetGroupEditTable.options.hideSetsCount && (
          <Box
            {...getColumnOptions(setsColumnWidth)}
            className={classes.headerTextWrapper}>
            <Typography variant="label-text-md">{t('common.sets')}</Typography>
          </Box>
        )}
        <Box
          {...getColumnOptions(columnWidth)}
          className={classes.headerTextWrapper}>
          <Typography variant="label-text-md">{t('common.rest')}</Typography>
        </Box>
        {set?.exercise?.splitBySide && (
          <Box {...getColumnOptions(sideColumnWidth)} />
        )}

        {orderedOptions?.map((setOption) => (
          <SetItemsListHeaderColumn
            key={setOption.optionId}
            exerciseOption={exerciseOptions.find(
              (e) => e.id === setOption.optionId,
            )}
            columnWidth={columnWidth}
          />
        ))}
        {useSetGroupEditTable.options.showSetItemStatus && (
          <Box
            {...getColumnOptions(actionsWidth)}
            className={classes.headerTextWrapper}
          />
        )}
      </Box>
      {set?.[setItemsFieldName]?.map((setItem) => (
        <React.Fragment key={getSetItemKey(setItem)}>
          <SetItemEdit
            columnWidth={columnWidth}
            set={set}
            setItem={setItem}
            useSetGroupEditTable={useSetGroupEditTable}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

function SetItemsListHeaderColumn({
  exerciseOption,
  columnWidth,
}: {
  exerciseOption: IExerciseOption;
  columnWidth: number;
}) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  return (
    <Box
      {...getColumnOptions(columnWidth)}
      key={exerciseOption?.nameKey}
      className={classes.headerTextWrapper}>
      <Typography variant="label-text-md">{exerciseOption?.name}</Typography>
    </Box>
  );
}

export default SetItemsListEdit;
